import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Language {
  code: string;
  label: string;
  shortLabel: string;
  flag: string;
  countryCode: string;
}

const LANGUAGES: Language[] = [
  {
    code: 'pt-BR',
    label: 'Português (BR)',
    shortLabel: 'PT',
    flag: 'https://flagcdn.com/w40/br.png',
    countryCode: 'BR',
  },
  {
    code: 'es',
    label: 'Español',
    shortLabel: 'ES',
    flag: 'https://flagcdn.com/w40/es.png',
    countryCode: 'ES',
  },
  {
    code: 'en',
    label: 'English',
    shortLabel: 'EN',
    flag: 'https://flagcdn.com/w40/us.png',
    countryCode: 'US',
  },
  {
    code: 'fr',
    label: 'Français',
    shortLabel: 'FR',
    flag: 'https://flagcdn.com/w40/fr.png',
    countryCode: 'FR',
  },
  {
    code: 'de',
    label: 'Deutsch',
    shortLabel: 'DE',
    flag: 'https://flagcdn.com/w40/de.png',
    countryCode: 'DE',
  },
  {
    code: 'it',
    label: 'Italiano',
    shortLabel: 'IT',
    flag: 'https://flagcdn.com/w40/it.png',
    countryCode: 'IT',
  },
  {
    code: 'ja',
    label: '日本語',
    shortLabel: 'JA',
    flag: 'https://flagcdn.com/w40/jp.png',
    countryCode: 'JP',
  },
];

interface LanguageSelectorProps {
  /** 'dark' for light backgrounds (public nav), 'light' for dark backgrounds (dashboard) */
  variant?: 'dark' | 'light';
  /** compact: show only flag + short code; full: show flag + full label */
  size?: 'compact' | 'full';
}

export default function LanguageSelector({ variant = 'light', size = 'compact' }: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [flashing, setFlashing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language)
    || LANGUAGES.find((l) => i18n.language?.startsWith(l.code.split('-')[0]))
    || LANGUAGES[0];

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (lang: Language) => {
    if (lang.code === i18n.language) {
      setOpen(false);
      return;
    }
    // Trigger flash overlay
    setFlashing(true);
    setTimeout(() => {
      i18n.changeLanguage(lang.code);
      localStorage.setItem('i18n_lang', lang.code);
    }, 80);
    setTimeout(() => setFlashing(false), 680);
    setOpen(false);
  };

  const triggerBase =
    variant === 'light'
      ? 'bg-white/5 border border-white/10 hover:bg-white/10 text-[#F5F5F0]'
      : 'bg-black/10 border border-white/20 hover:bg-black/20 text-white';

  const dropdownBase =
    variant === 'light'
      ? 'bg-[#0F0F18] border border-white/10'
      : 'bg-[#0F0F18] border border-white/15';

  return (
    <>
      {/* Language transition overlay */}
      {flashing && <div className="lang-transition-overlay" aria-hidden="true" />}

      <div className="relative" ref={ref}>
        {/* Trigger Button */}
        <button
          onClick={() => setOpen(!open)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${triggerBase}`}
          aria-label="Selecionar idioma"
          title="Selecionar idioma"
        >
          {/* Flag circle */}
          <span
            className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center shrink-0 border border-white/10"
            aria-hidden="true"
          >
            <img
              src={currentLang.flag}
              alt={currentLang.label}
              className="w-full h-full object-cover"
            />
          </span>
          {size === 'compact' ? (
            <span className="text-xs font-semibold tracking-wide">{currentLang.shortLabel}</span>
          ) : (
            <span className="text-sm font-medium">{currentLang.label}</span>
          )}
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`}></i>
          </span>
        </button>

        {/* Dropdown */}
        {open && (
          <div
            className={`absolute right-0 top-full mt-2 w-44 rounded-xl shadow-2xl overflow-hidden z-[200] ${dropdownBase}`}
          >
            <div className="py-1">
              {LANGUAGES.map((lang) => {
                const isActive = currentLang.code === lang.code;
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleSelect(lang)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-[#D4AF37]/15 text-[#D4AF37]'
                        : 'text-[#B8B8B0] hover:bg-white/5 hover:text-[#F5F5F0]'
                    }`}
                  >
                    <span
                      className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center shrink-0 border border-white/10"
                      aria-hidden="true"
                    >
                      <img
                        src={lang.flag}
                        alt={lang.label}
                        className="w-full h-full object-cover"
                      />
                    </span>
                    <span className="flex-1 text-left font-medium">{lang.label}</span>
                    {isActive && (
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-line text-[#D4AF37] text-sm"></i>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
